import React, { memo, useMemo, useState } from 'react';

import { AdjustPopoverOverflow, BringgPopover, Button, DropdownMenu, Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { SearchInput } from '@bringg-frontend/ui';

import styles from './template-tag.module.scss';

export type PlaceholderData = {
	type: string;
	display_name?: string;
	info: string;
	values?: PlaceholderData[];
	disabled?: boolean;
	searchable?: boolean;
};

// Injected so this shared component stays translation-agnostic; each consumer supplies its own namespace.
export interface SearchableGroupCopy {
	searchPlaceholder: string;
	noMatches: string;
}

interface TemplateTagProps {
	labels: PlaceholderData[];
	textDescription: string;
	onClick: (type: string) => void;
	// Omit to render searchable groups as plain dropdowns, so no consumer shows untranslated copy.
	searchableGroupCopy?: SearchableGroupCopy;
}

const tooltipInBody = () => document.body;
const TOOLTIP_Z_INDEX = 10000;

const SearchableValuesDropdown = ({
	label,
	onClick,
	copy
}: {
	label: PlaceholderData;
	onClick: (type: string) => void;
	copy: SearchableGroupCopy;
}) => {
	const [query, setQuery] = useState('');
	const [visible, setVisible] = useState(false);

	const filteredValues = useMemo(() => {
		const trimmedQuery = query.trim().toLowerCase();
		if (!trimmedQuery) {
			return label.values;
		}
		return label.values.filter(value => (value.display_name ?? value.type).toLowerCase().includes(trimmedQuery));
	}, [label.values, query]);

	const onVisibleChange = (isVisible: boolean) => {
		setVisible(isVisible);
		if (!isVisible) {
			setQuery('');
		}
	};

	return (
		<BringgPopover
			adjustOverflow={AdjustPopoverOverflow.ADJUST_INTO_VIEW}
			popoverType="dropdown"
			trigger={['click']}
			visible={visible}
			onVisibleChange={onVisibleChange}
			content={
				<div className={styles.searchableDropdown}>
					<SearchInput
						autoFocus
						value={query}
						placeholder={copy.searchPlaceholder}
						onChange={setQuery}
						data-test-id={`${label.type}-search`}
					/>
					<div className={styles.searchableDropdownList}>
						{filteredValues.length ? (
							filteredValues.map(({ type, display_name, info, disabled }) => {
								const selectValue = () => {
									if (disabled) {
										return;
									}
									onClick(type);
									onVisibleChange(false);
								};

								return (
									<Tooltip
										title={info}
										key={type}
										getPopupContainer={tooltipInBody}
										zIndex={TOOLTIP_Z_INDEX}
									>
										<div
											role="menuitem"
											aria-disabled={disabled}
											tabIndex={disabled ? -1 : 0}
											data-test-id={type}
											className={`${styles.searchableDropdownItem} ${
												disabled ? styles.disabled : ''
											}`}
											onClick={selectValue}
											onKeyDown={event => {
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault();
													selectValue();
												}
											}}
										>
											{display_name ?? type}
										</div>
									</Tooltip>
								);
							})
						) : (
							<div className={styles.searchableDropdownEmpty}>{copy.noMatches}</div>
						)}
					</div>
				</div>
			}
		>
			<Button className={styles.labelButton} data-test-id={label.type}>
				<div>{label.display_name ?? label.type}</div>
				<BringgIcon iconName={BringgFontIcons.Chevron} />
			</Button>
		</BringgPopover>
	);
};

export const TemplateTag = memo(({ textDescription, labels, onClick, searchableGroupCopy }: TemplateTagProps) => {
	return (
		<>
			<div className={styles.description}>{textDescription}</div>
			<div className={styles.labelContainer}>
				{labels.map(label => (
					<Tooltip title={label.info} key={label.type}>
						{label.values ? (
							label.searchable && searchableGroupCopy ? (
								<SearchableValuesDropdown label={label} onClick={onClick} copy={searchableGroupCopy} />
							) : (
								<DropdownMenu
									adjustOverflow={AdjustPopoverOverflow.ADJUST_INTO_VIEW}
									trigger={['click']}
									items={label.values.map(({ type, display_name, info, disabled }) => ({
										key: type,
										disabled,
										label: (
											<Tooltip
												title={info}
												key={type}
												getPopupContainer={tooltipInBody}
												zIndex={TOOLTIP_Z_INDEX}
											>
												{display_name ?? type}
											</Tooltip>
										),
										onClick: () => onClick(type)
									}))}
								>
									<Button className={styles.labelButton} data-test-id={label.type}>
										<div>{label.display_name ?? label.type}</div>
										<BringgIcon iconName={BringgFontIcons.Chevron} />
									</Button>
								</DropdownMenu>
							)
						) : (
							<Button
								className={styles.labelButton}
								data-test-id={label.type}
								onClick={() => onClick(label.type)}
							>
								{label.display_name ?? label.type}
							</Button>
						)}
					</Tooltip>
				))}
			</div>
		</>
	);
});
